const DUPLICATE_NOTES_SELECTION_KEY = "gomake.duplicateJobNotesSelection";
export const PENDING_QUOTE_CONVERSION_NOTES_KEY =
  "gomake.pendingQuoteConversionNotes";
/** One-shot flag: clear quote/order document Notes after duplicate without notes */
export const PENDING_CLEAR_DOCUMENT_NOTES_KEY =
  "gomake.pendingClearDocumentNotes";

export interface SelectedDuplicateNote {
  noteType: number;
  name: string | null;
  value: string;
  printingActionId: string | null;
}

export interface DuplicateNotesSelection {
  ignoreAll: boolean;
  /** Full selected notes (general + action). */
  selectedNotes: SelectedDuplicateNote[];
  /** Selected action printingActionIds */
  keepActionNoteIds: string[];
  /** Selected general note text values */
  selectedGeneralNoteValues: string[];
  /** All action note ids that existed in the modal (for partial detection) */
  allActionNoteIds: string[];
  /**
   * When true: backend stripped all action notes; frontend must re-apply
   * selected action notes after the new job is created (partial selection).
   * When false: backend copied action notes via ignoreActionNotes=false.
   */
  needsActionNotesReapply: boolean;
  productType?: string;
}

type DuplicateNotesSelectionMap = Record<string, DuplicateNotesSelection>;

const GENERAL_NOTE_TYPE = 0;

const canUseSessionStorage = () =>
  typeof window !== "undefined" && Boolean(window.sessionStorage);

const readSelectionMap = (): DuplicateNotesSelectionMap => {
  if (!canUseSessionStorage()) return {};

  try {
    const rawValue = window.sessionStorage.getItem(DUPLICATE_NOTES_SELECTION_KEY);
    return rawValue ? JSON.parse(rawValue) : {};
  } catch {
    return {};
  }
};

const writeSelectionMap = (selectionMap: DuplicateNotesSelectionMap) => {
  if (!canUseSessionStorage()) return;

  window.sessionStorage.setItem(
    DUPLICATE_NOTES_SELECTION_KEY,
    JSON.stringify(selectionMap)
  );
};

const uniqueIds = (ids: Array<string | null | undefined>) =>
  Array.from(new Set(ids.filter(Boolean) as string[]));

export const getActionNoteIds = (
  notes: Array<{ noteType?: number; printingActionId?: string | null }>
) =>
  uniqueIds(
    notes
      .filter(
        (note) =>
          note?.noteType !== GENERAL_NOTE_TYPE && Boolean(note?.printingActionId)
      )
      .map((note) => note.printingActionId)
  );

const normalizeSelection = (
  selection: Partial<DuplicateNotesSelection> & {
    ignoreAll?: boolean;
  }
): DuplicateNotesSelection => {
  const selectedNotes = Array.isArray(selection.selectedNotes)
    ? selection.selectedNotes.filter(
        (note) => String(note?.value ?? "").trim().length > 0
      )
    : [];

  const keepActionNoteIds =
    selectedNotes.length > 0
      ? getActionNoteIds(selectedNotes)
      : uniqueIds(selection.keepActionNoteIds ?? []);

  const selectedGeneralNoteValues =
    selectedNotes.length > 0
      ? selectedNotes
          .filter((note) => note.noteType === GENERAL_NOTE_TYPE)
          .map((note) => String(note.value ?? ""))
          .filter((value) => value.trim().length > 0)
      : (selection.selectedGeneralNoteValues ?? []).filter(
          (value) => String(value ?? "").trim().length > 0
        );

  const allActionNoteIds = uniqueIds(selection.allActionNoteIds ?? []);
  const ignoreAll =
    Boolean(selection.ignoreAll) ||
    (selectedNotes.length === 0 &&
      selectedGeneralNoteValues.length === 0 &&
      keepActionNoteIds.length === 0);

  const hasPartialActionSelection =
    keepActionNoteIds.length > 0 &&
    allActionNoteIds.length > keepActionNoteIds.length;

  return {
    ignoreAll,
    selectedNotes,
    keepActionNoteIds,
    selectedGeneralNoteValues,
    allActionNoteIds,
    needsActionNotesReapply:
      selection.needsActionNotesReapply ?? hasPartialActionSelection,
    productType: selection.productType,
  };
};

/**
 * Builds session selection + resolves ignoreActionNotes strategy:
 * - no action notes selected → ignoreActionNotes=true (strip all)
 * - all action notes selected → ignoreActionNotes=false (backend keeps all)
 * - partial action notes → ignoreActionNotes=true + re-apply selected later
 */
export const buildDuplicateNotesSelection = (
  selectedNotes: SelectedDuplicateNote[],
  allAvailableNotes: SelectedDuplicateNote[],
  productType?: string
): { selection: DuplicateNotesSelection; ignoreActionNotes: boolean } => {
  const allActionNoteIds = getActionNoteIds(allAvailableNotes);
  const selection = normalizeSelection({
    ignoreAll: selectedNotes.length === 0,
    selectedNotes,
    keepActionNoteIds: [],
    selectedGeneralNoteValues: [],
    allActionNoteIds,
    productType,
  });

  const hasPartialActionSelection =
    selection.keepActionNoteIds.length > 0 &&
    allActionNoteIds.length > selection.keepActionNoteIds.length;

  const ignoreActionNotes =
    selection.ignoreAll ||
    selection.keepActionNoteIds.length === 0 ||
    hasPartialActionSelection;

  return {
    selection: {
      ...selection,
      needsActionNotesReapply: hasPartialActionSelection,
    },
    ignoreActionNotes,
  };
};

export const saveDuplicateNotesSelection = (
  orderItemId: string | undefined,
  selection: DuplicateNotesSelection
) => {
  if (!orderItemId) return;

  const selectionMap = readSelectionMap();
  selectionMap[orderItemId] = normalizeSelection(selection);
  writeSelectionMap(selectionMap);
};

export const readDuplicateNotesSelection = (
  orderItemId: string | undefined
): DuplicateNotesSelection | null => {
  if (!orderItemId) return null;

  const selectionMap = readSelectionMap();
  const selection = selectionMap[orderItemId];
  return selection ? normalizeSelection(selection) : null;
};

export const clearDuplicateNotesSelection = (
  orderItemId: string | undefined
) => {
  if (!orderItemId) return;

  const selectionMap = readSelectionMap();
  if (!selectionMap[orderItemId]) return;

  delete selectionMap[orderItemId];
  writeSelectionMap(selectionMap);
};

export const readAndClearDuplicateNotesSelection = (
  orderItemId: string | undefined
): DuplicateNotesSelection | null => {
  const selection = readDuplicateNotesSelection(orderItemId);
  if (selection) {
    clearDuplicateNotesSelection(orderItemId);
  }
  return selection;
};

export const hasDuplicateNotesToApply = (
  selection: DuplicateNotesSelection | null | undefined
): boolean =>
  Boolean(
    selection &&
      !selection.ignoreAll &&
      (selection.selectedGeneralNoteValues?.length > 0 ||
        (selection.needsActionNotesReapply &&
          selection.keepActionNoteIds?.length > 0) ||
        (selection.needsActionNotesReapply &&
          selection.selectedNotes?.some(
            (note) => note.noteType !== GENERAL_NOTE_TYPE
          )))
  );

/**
 * Whether we must call add-selected-notes after the new job is created:
 * - ignoreAll: strip leftover section notes (Cover / Internal pages) that
 *   ignoreActionNotes did not remove (they often have no printingActionId)
 * - otherwise: re-apply selected general / partial action notes
 */
export const needsDuplicateNotesSync = (
  selection: DuplicateNotesSelection | null | undefined
): selection is DuplicateNotesSelection =>
  Boolean(
    selection && (selection.ignoreAll || hasDuplicateNotesToApply(selection))
  );

/** Notes to POST after job create (general always; action only when re-apply needed) */
export const mapSelectionToAddNotesPayload = (
  selection: DuplicateNotesSelection
): Array<{
  note: string;
  isFromProductionFloor: boolean;
  printingActionId?: string | null;
  noteType?: number;
  name?: string | null;
}> => {
  // Empty payload tells the backend to keep no notes (clears leftover section notes)
  if (selection.ignoreAll) return [];

  const notes =
    selection.selectedNotes?.length > 0
      ? selection.selectedNotes
      : (selection.selectedGeneralNoteValues ?? []).map((value) => ({
          noteType: GENERAL_NOTE_TYPE,
          name: null,
          value,
          printingActionId: null,
        }));

  return notes
    .filter((note) => {
      const isGeneral = note.noteType === GENERAL_NOTE_TYPE;
      if (isGeneral) return String(note.value ?? "").trim().length > 0;
      return (
        selection.needsActionNotesReapply &&
        String(note.value ?? "").trim().length > 0
      );
    })
    .map((note) => ({
      note: String(note.value ?? ""),
      isFromProductionFloor: true,
      printingActionId: note.printingActionId,
      noteType: note.noteType,
      name: note.name,
    }));
};

type PendingQuoteConversionNotes = DuplicateNotesSelection & {
  quoteDocumentId?: string | null;
  createdAt?: number;
};

type PendingClearDocumentNotes = {
  documentId?: string | null;
  createdAt: number;
};

const PENDING_NOTES_TTL_MS = 30 * 60 * 1000; // 30 minutes

const isPendingExpired = (createdAt?: number) =>
  typeof createdAt === "number" && Date.now() - createdAt > PENDING_NOTES_TTL_MS;

export const savePendingQuoteConversionNotes = (
  selection: DuplicateNotesSelection,
  quoteDocumentId?: string | null
) => {
  if (!canUseSessionStorage()) return;

  const payload: PendingQuoteConversionNotes = {
    ...normalizeSelection(selection),
    quoteDocumentId: quoteDocumentId || null,
    createdAt: Date.now(),
  };

  window.sessionStorage.setItem(
    PENDING_QUOTE_CONVERSION_NOTES_KEY,
    JSON.stringify(payload)
  );
};

export const readPendingQuoteConversionNotes = (
  currentQuoteDocumentId?: string | null
): DuplicateNotesSelection | null => {
  if (!canUseSessionStorage()) return null;

  try {
    const rawValue = window.sessionStorage.getItem(
      PENDING_QUOTE_CONVERSION_NOTES_KEY
    );
    if (!rawValue) return null;

    const parsed = JSON.parse(rawValue) as PendingQuoteConversionNotes;
    if (isPendingExpired(parsed.createdAt)) {
      clearPendingQuoteConversionNotes();
      return null;
    }

    // Unscoped pending (no quote id at save time) — do not apply to any convert;
    // wrong-job sync is worse than leaving notes for a rare edge case.
    if (!parsed.quoteDocumentId) {
      clearPendingQuoteConversionNotes();
      return null;
    }

    // Only apply to the quote that created the pending payload.
    if (
      !currentQuoteDocumentId ||
      String(parsed.quoteDocumentId) !== String(currentQuoteDocumentId)
    ) {
      return null;
    }

    return normalizeSelection(parsed);
  } catch {
    return null;
  }
};

export const clearPendingQuoteConversionNotes = () => {
  if (!canUseSessionStorage()) return;

  window.sessionStorage.removeItem(PENDING_QUOTE_CONVERSION_NOTES_KEY);
};

export const readAndClearPendingQuoteConversionNotes = (
  currentQuoteDocumentId?: string | null
): DuplicateNotesSelection | null => {
  const selection = readPendingQuoteConversionNotes(currentQuoteDocumentId);
  if (selection) {
    clearPendingQuoteConversionNotes();
  }
  return selection;
};

export const savePendingClearDocumentNotes = (documentId?: string | null) => {
  if (!canUseSessionStorage()) return;
  const payload: PendingClearDocumentNotes = {
    documentId: documentId || null,
    createdAt: Date.now(),
  };
  window.sessionStorage.setItem(
    PENDING_CLEAR_DOCUMENT_NOTES_KEY,
    JSON.stringify(payload)
  );
};

/**
 * One-shot clear for quote document Notes after "duplicate without notes".
 * Returns true only when pending exists, is fresh, and matches the loaded document
 * (or was unscoped and the caller explicitly allows unscoped clear — quote cart only).
 */
export const consumePendingClearDocumentNotes = (
  loadedDocumentId?: string | null,
  options?: { allowUnscoped?: boolean }
): boolean => {
  if (!canUseSessionStorage()) return false;

  try {
    const rawValue = window.sessionStorage.getItem(
      PENDING_CLEAR_DOCUMENT_NOTES_KEY
    );
    if (!rawValue) return false;

    // Legacy boolean flag from older builds — only honor on quote cart.
    if (rawValue === "true") {
      if (!loadedDocumentId || !options?.allowUnscoped) {
        return false;
      }
      window.sessionStorage.removeItem(PENDING_CLEAR_DOCUMENT_NOTES_KEY);
      return true;
    }

    const parsed = JSON.parse(rawValue) as PendingClearDocumentNotes;
    if (isPendingExpired(parsed.createdAt)) {
      window.sessionStorage.removeItem(PENDING_CLEAR_DOCUMENT_NOTES_KEY);
      return false;
    }

    if (!loadedDocumentId) {
      // Keep pending until a real document loads (do not consume yet).
      return false;
    }

    if (parsed.documentId) {
      if (String(parsed.documentId) !== String(loadedDocumentId)) {
        // Wrong document — leave pending for the intended quote.
        return false;
      }
    } else if (!options?.allowUnscoped) {
      // Unscoped pending must not wipe a random opened quote/order.
      return false;
    }

    window.sessionStorage.removeItem(PENDING_CLEAR_DOCUMENT_NOTES_KEY);
    return true;
  } catch {
    window.sessionStorage.removeItem(PENDING_CLEAR_DOCUMENT_NOTES_KEY);
    return false;
  }
};
